import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchReguaAlerta, ReguaAlertaResponse } from '../../../api/contac/contacAnalyticsApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
// Esqueleto de página compartilhado — import explícito, nunca depender do CSS de outra tela
// (lição do BUG-V74).
import '../../recepcao/pages/Recepcao.css';
import '../../contac/resultado_por_placa/pages/ResultadoPorPlaca.css'; // bolinhas da legenda
import './css/adminSistema.css';

/**
 * Administração do Sistema → Contac (contac/at-13, ADR 0007 do Contac).
 *
 * ## Por que esta tela existe, e por que ela é assim
 *
 * O Victor pediu que a regra das cores ficasse explicada aqui **em nível de apresentação para a
 * diretoria**: o que é verde, laranja e vermelho, e por que a faixa depende dos meses. Então esta
 * não é uma tela de configuração com um formulário — é a explicação da régua, escrita para ser
 * lida em voz alta numa reunião, com os números que a sustentam e a data em que foram medidos.
 *
 * 🔴 **Todo número vem do backend** (`GET /contac-analytics/regua-alerta`), incluindo as medições.
 * Copiar valores para dentro do texto faria a explicação divergir do que o sistema aplica no
 * primeiro ajuste — e quem apresentasse estaria descrevendo uma régua que não existe mais.
 *
 * Só leitura nesta entrega: mudar o valor por mês é decisão de produto, feita em código e
 * congelada em teste (o drawer diz isso).
 */

const emReais = (valor: number, casas = 0) =>
  valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });

const emPorcento = (valor: number) =>
  `${valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta tela é',
    conteudo: (
      <>
        <p>
          A explicação da <strong>régua de cores</strong> usada no Dashboard de Placas do perfil de
          Relacionamento, escrita para ser apresentada — inclusive à diretoria. Cada número aqui vem
          do próprio sistema, com a data em que foi medido.
        </p>
        <p>
          Ela também resume as regras do <strong>Radar do Relacionamento</strong>, o painel que
          aponta quem está com boleto em aberto.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que é só leitura',
    conteudo: (
      <>
        <p>
          O valor por mês da régua é uma <strong>decisão de produto</strong>, tomada com base em
          medição da carteira inteira. Mudá-lo altera a cor de dezenas de milhares de placas de uma
          vez, então ele vive no código e é protegido por teste: alterar exige uma entrega, com
          alguém decidindo e registrando o porquê.
        </p>
        <p>
          Se a régua precisar mudar, o caminho é acionar a TI com a justificativa — não existe botão
          aqui que faça isso em silêncio.
        </p>
      </>
    ),
  },
  {
    titulo: 'Onde cada regra aparece',
    conteudo: (
      <p>
        A régua age no <strong>Dashboard de Placas</strong>, somente para o perfil de
        Relacionamento, que vê cores no lugar dos valores. As regras do Radar aparecem no painel do
        topo daquela mesma tela. Os dois têm o próprio botão <strong>?</strong> com o resumo.
      </p>
    ),
  },
];

const ContacConfigPage: React.FC = () => {
  const [regua, setRegua] = useState<ReguaAlertaResponse | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [mesesSimulados, setMesesSimulados] = useState(6);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setRegua(await fetchReguaAlerta());
      setErro(false);
    } catch (error) {
      // Estado seguro + sinal (at-60): esta tela é feita de números: mostrá-la vazia, sem dizer
      // que a consulta falhou, faria alguém apresentar uma régua que não conferiu.
      setRegua(null);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar a régua das cores. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const bandaSimulada = useMemo(
    () => (regua ? regua.rPorMes * Math.max(1, mesesSimulados) : 0),
    [regua, mesesSimulados],
  );

  const saudavel = regua?.ancoragem.resultadoMensal.find((r) =>
    r.situacao.startsWith('Pagou e não teve'),
  );
  const semPagar = regua?.ancoragem.resultadoMensal.find((r) => r.situacao.startsWith('Não pagou'));
  const boleto = regua?.ancoragem.componentes[0];
  const rateio = regua?.ancoragem.componentes[1];
  const umMes = regua?.ancoragem.distribuicaoPorCorte.find((d) => d.meses === 1);

  return (
    <div
      className="Frame1000004012"
      style={{ height: 'fit-content', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
    >
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Contac
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
            >
              ?
            </button>
          </div>
        </div>

        <p className="admin-hint">
          Como o sistema decide a <strong>cor</strong> de cada placa para o perfil de Relacionamento,
          e as regras do <strong>Radar do Relacionamento</strong>. Esta tela é só leitura: os números
          abaixo são os que o sistema aplica hoje.
        </p>

        {carregando && <FullScreenLoader />}

        {erro && (
          <p className="erro-de-tela">
            Não foi possível carregar a régua das cores. Esta tela não está mostrando os valores em
            uso — ela não conseguiu consultá-los. Recarregue a página; se persistir, acione a TI.
          </p>
        )}

        {regua && (
          <>
            {/* 1 ------------------------------------------------------------------ */}
            <div className="admin-card" style={{ marginTop: 16 }}>
              <div className="admin-card-title">1. O que a cor responde</div>
              <p>
                A cor mostra o <strong>resultado da placa no período consultado</strong>: o que ela
                trouxe de mensalidade paga, menos o que custou — a parte dela nas despesas gerais e
                qualquer custo direto, como um reparo.
              </p>
              <p style={{ fontWeight: 600 }}>
                Ela responde "a placa deu lucro?", e não "o associado pagou?".
              </p>
              <p>
                Quem deixa de pagar não gera receita, mas também <strong>não recebe</strong> a parte
                dele nas despesas. O resultado fica em torno de <strong>zero</strong> — ou seja,
                laranja, igual a uma placa equilibrada. Foi medido: placas que não pagaram nada no
                mês tiveram resultado mediano de{' '}
                <strong>{semPagar ? emReais(semPagar.mediana, 2) : '-'}</strong>.
              </p>
              <p>
                Por isso a tela do Relacionamento tem a coluna <strong>Pagamento</strong> (em quantos
                meses do período houve boleto pago) e o <strong>Radar</strong> no topo. É lá que a
                inadimplência aparece, não na cor.
              </p>
            </div>

            {/* 2 ------------------------------------------------------------------ */}
            <div className="admin-card" style={{ marginTop: 16 }}>
              <div className="admin-card-title">2. As três cores</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span className="bola-resultado bola-green" style={{ marginTop: 4, flex: '0 0 auto' }} />
                  <div>
                    <strong>Verde — resultado acima da faixa.</strong> A placa pagou e o que ela
                    trouxe cobriu o custo dela no período.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span className="bola-resultado bola-orange" style={{ marginTop: 4, flex: '0 0 auto' }} />
                  <div>
                    <strong>Laranja — dentro da faixa, perto de zero.</strong> Inclui dois casos
                    diferentes: a placa que se equilibrou no período e a placa que{' '}
                    <strong>não pagou nada</strong> (que fica em zero). A coluna Pagamento separa os
                    dois.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span className="bola-resultado bola-red" style={{ marginTop: 4, flex: '0 0 auto' }} />
                  <div>
                    <strong>Vermelho — resultado abaixo da faixa.</strong> O custo superou o que a
                    placa trouxe. Na grande maioria dos casos isso é <strong>evento</strong>, não
                    falta de pagamento: entre as placas vermelhas num período de seis meses,{' '}
                    <strong>
                      {regua.ancoragem.vermelhoComCustoDireto.find((v) => v.meses === 6)?.percentual ?? '-'}%
                    </strong>{' '}
                    tiveram custo direto relevante (reparo, assistência).
                  </div>
                </div>
              </div>
            </div>

            {/* 3 ------------------------------------------------------------------ */}
            <div className="admin-card" style={{ marginTop: 16 }}>
              <div className="admin-card-title">3. A faixa cresce com os meses</div>
              <p
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  background: 'var(--tema-fundo, #F7F7F7)',
                  padding: '12px 16px',
                  borderRadius: 8,
                  display: 'inline-block',
                }}
              >
                {regua.formula}
              </p>
              <p>
                Uma placa em dia sobra, por mês, algo entre{' '}
                <strong>{saudavel ? emReais(saudavel.p25) : '-'}</strong> e{' '}
                <strong>{saudavel ? emReais(saudavel.p75) : '-'}</strong> — porque o boleto mediano é{' '}
                <strong>{boleto ? emReais(boleto.mediana) : '-'}</strong> e a parte dela nas despesas
                fica em torno de <strong>{rateio ? emReais(rateio.mediana) : '-'}</strong>.
              </p>
              <p>
                Com a faixa fixa de {emReais(regua.ancoragem.faixaAnterior)} que valia antes, um
                período de <strong>um mês</strong> deixava{' '}
                <strong>{umMes ? emPorcento(100 - umMes.verdeAntes) : '-'}</strong> das placas em dia
                pintadas de laranja: o usuário lia "perto de zero" onde havia lucro normal. Uma faixa
                que cresce com o período resolve isso sem mudar a leitura de períodos longos.
              </p>

              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Período consultado</th>
                      <th style={{ textAlign: 'left' }}>Faixa do laranja</th>
                      <th style={{ textAlign: 'left' }}>Verde acima de</th>
                      <th style={{ textAlign: 'left' }}>Vermelho abaixo de</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regua.cortes.map((corte) => (
                      <tr key={corte.meses}>
                        <td>{corte.meses === 1 ? '1 mês' : `${corte.meses} meses`}</td>
                        <td>
                          {emReais(-corte.banda)} a {emReais(corte.banda)}
                        </td>
                        <td>{emReais(corte.verdeAcimaDe)}</td>
                        <td>{emReais(corte.vermelhoAbaixoDe)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <label htmlFor="meses-simulados" style={{ fontWeight: 600 }}>
                  Ver a faixa de outro período:
                </label>
                <select
                  id="meses-simulados"
                  className="admin-input"
                  style={{ width: 120 }}
                  value={mesesSimulados}
                  onChange={(e) => setMesesSimulados(Number(e.target.value))}
                >
                  {Array.from({ length: 24 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {m === 1 ? '1 mês' : `${m} meses`}
                    </option>
                  ))}
                </select>
                <span>
                  verde acima de <strong>{emReais(bandaSimulada)}</strong>, vermelho abaixo de{' '}
                  <strong>{emReais(-bandaSimulada)}</strong>.
                </span>
              </div>
            </div>

            {/* 4 ------------------------------------------------------------------ */}
            <div className="admin-card" style={{ marginTop: 16 }}>
              <div className="admin-card-title">4. De onde vem o valor por mês</div>
              <p>
                O valor de <strong>{emReais(regua.rPorMes)} por mês</strong> não foi escolhido no
                chute: é o ponto abaixo do qual está o quarto menos lucrativo das placas saudáveis.
                Em bom português: três em cada quatro placas que pagam e não tiveram custo direto
                sobram mais que isso por mês, e passam a aparecer como verdes; o quarto restante
                continua em laranja, que é onde ele deve estar.
              </p>
              <p className="admin-texto-secundario">
                Medido em <strong>{regua.ancoragem.medidoEm}</strong> sobre a{' '}
                {regua.ancoragem.base}, competências de {regua.ancoragem.janelaMedida}.
              </p>

              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Período</th>
                      <th style={{ textAlign: 'left' }}>Placas analisadas</th>
                      <th style={{ textAlign: 'left' }}>Verdes com a régua antiga</th>
                      <th style={{ textAlign: 'left' }}>Verdes com a régua atual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regua.ancoragem.distribuicaoPorCorte.map((linha) => (
                      <tr key={linha.meses}>
                        <td>{linha.meses === 1 ? '1 mês' : `${linha.meses} meses`}</td>
                        <td>{linha.placas.toLocaleString('pt-BR')}</td>
                        <td>{emPorcento(linha.verdeAntes)}</td>
                        <td>
                          <strong>{emPorcento(linha.verdeDepois)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="admin-texto-secundario" style={{ marginTop: 8 }}>
                A tabela considera apenas placas que pagaram em <strong>todos</strong> os meses do
                período — ou seja, placas comprovadamente em dia. Repare na última linha: em doze
                meses a régua nova quase não muda nada, porque {regua.cortes[3]?.meses ?? 12} vezes{' '}
                {emReais(regua.rPorMes)} dá {emReais(regua.cortes[3]?.banda ?? 480)}, praticamente a
                faixa antiga. A mudança acontece onde havia problema: nos períodos curtos.
              </p>
            </div>

            {/* 5 ------------------------------------------------------------------ */}
            <div className="admin-card" style={{ marginTop: 16 }}>
              <div className="admin-card-title">5. Três exemplos</div>
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Situação</th>
                      <th style={{ textAlign: 'left' }}>Período</th>
                      <th style={{ textAlign: 'left' }}>Resultado</th>
                      <th style={{ textAlign: 'left' }}>Cor</th>
                      <th style={{ textAlign: 'left' }}>Pagamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Pagou o mês, sem custo direto</td>
                      <td>1 mês</td>
                      <td>{emReais(110)}</td>
                      <td>
                        <span className="bola-resultado bola-green" /> Verde
                      </td>
                      <td>1 de 1</td>
                    </tr>
                    <tr>
                      <td>Não pagou nenhum mês</td>
                      <td>3 meses</td>
                      <td>{emReais(0)}</td>
                      <td>
                        <span className="bola-resultado bola-orange" /> Laranja
                      </td>
                      <td>
                        <strong>0 de 3</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>Pagou tudo, mas teve um reparo</td>
                      <td>6 meses</td>
                      <td>{emReais(-1320)}</td>
                      <td>
                        <span className="bola-resultado bola-red" /> Vermelho
                      </td>
                      <td>6 de 6</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="admin-texto-secundario" style={{ marginTop: 8 }}>
                A linha do meio é a que mais confunde: o associado não pagou nada em três meses e a
                placa aparece <strong>laranja</strong>. É por isso que a coluna Pagamento existe.
              </p>
            </div>

            {/* 6 ------------------------------------------------------------------ */}
            <div className="admin-card" style={{ marginTop: 16 }}>
              <div className="admin-card-title">6. O que mudou, e quando</div>
              <p>
                Até a versão <strong>{regua.vigenteDesde}</strong>, a faixa do laranja era fixa em{' '}
                {emReais(regua.ancoragem.faixaAnterior)} para qualquer período consultado. A partir
                dela, a faixa é proporcional ao número de meses.
              </p>
              <p>
                <strong>Nada foi recalculado</strong>: os valores financeiros do Contac são os
                mesmos de sempre, e continuam conferíveis nas demais telas. O que mudou foi apenas
                como o resultado vira cor para o perfil de Relacionamento.
              </p>
            </div>

            {/* 7 ------------------------------------------------------------------ */}
            <div className="admin-card" style={{ marginTop: 16 }}>
              <div className="admin-card-title">7. O Radar do Relacionamento</div>
              <p>
                No topo do Dashboard de Placas, o perfil de Relacionamento tem um painel com{' '}
                <strong>quem está com boleto em aberto e vencido</strong>, agrupado por há quanto
                tempo o atraso começou: até 30 dias, de 31 a 90, quem acumula dois ou mais boletos, e
                quem regularizou nos últimos 30 dias.
              </p>
              <p>
                <strong>Boleto cancelado não conta como dívida.</strong> Quando um boleto é
                substituído, o antigo fica registrado como cancelado e nunca recebe pagamento — mas o
                associado pagou o novo. Em medição de {regua.ancoragem.medidoEm}, 93 de cada 100
                boletos "vencidos e sem pagamento" estavam apenas cancelados. Por isso o Radar conta
                somente boleto em aberto.
              </p>
              <p>
                O painel <strong>não segue os filtros da tela</strong>: ele olha a carteira inteira,
                com os boletos vencidos até ontem. E os dados dele são mais recentes que os da lista
                — os boletos são sincronizados todos os dias, enquanto o consolidado financeiro fecha
                uma vez por mês.
              </p>
              <p>
                O texto de priorização é escrito por inteligência artificial a partir das{' '}
                <strong>quantidades</strong>: ela não calcula nada e não recebe valor em dinheiro. O
                custo dessas chamadas aparece na tela de <strong>Consumo de IA</strong>.
              </p>
            </div>
          </>
        )}
      </div>

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Contac"
        secoes={helpSections}
      />
    </div>
  );
};

export default ContacConfigPage;
